// Main-thread client for the DEM corridor solver. We keep tile-fetching here (so it
// goes through the existing Dexie tile cache and stays offline-capable), enumerate the
// tiles the corridor will touch, hand their raw bytes to the worker, and await the path.
// This mirrors the route-planner convention: gather data on main, solve in the worker.
import DemRouteWorker from './dem-route-worker.ts?worker'
import { demTileCache } from '../terrain/dem-tile-cache.js'
import {
  type LL,
  type DemTuning,
  DEM_DEFAULTS,
  DEM_Z,
  TILE,
  lngToGX,
  latToGY,
  metersPerPx,
  tileKey,
} from './dem-geo.js'
import type { DemTile, DemRouteRequest, DemRouteResponse } from './dem-route-worker.js'
import { logger } from '../../utils/logger.js'

const log = logger.create('map')

let worker: Worker | null = null
let counter = 0
const pending = new Map<number, (path: LL[] | null) => void>()

function ensureWorker(): Worker {
  if (worker) return worker
  const w = new DemRouteWorker()
  w.onmessage = (e: MessageEvent<DemRouteResponse>): void => {
    const resolve = pending.get(e.data.id)
    if (resolve) {
      pending.delete(e.data.id)
      resolve(e.data.path)
    }
  }
  w.onerror = (e): void => {
    log.warn(`[snap] DEM worker error: ${e.message}`)
    // Fail open: resolve everything in flight to null and drop the worker so the
    // next call rebuilds it. Callers treat null as "no route" and fall back.
    for (const resolve of pending.values()) resolve(null)
    pending.clear()
    worker = null
  }
  worker = w
  return w
}

/**
 * The z16 tiles the corridor A* may read for a `start -> goal` leg: every tile the
 * straight centerline passes through, plus its 8 neighbours (the solver reads across
 * tile edges). Walking by ~`bufCells` px guarantees no along-track gap, and the 3x3
 * stamp covers the (thin) perpendicular buffer — matching the original lazy loader's
 * footprint, just computed up front so we can fetch in parallel.
 */
function corridorTileKeys(start: LL, goal: LL, tune: DemTuning): Array<[number, number]> {
  const { bufferM } = { ...DEM_DEFAULTS, ...tune }
  const lat0 = (start[1] + goal[1]) / 2
  const bufCells = bufferM / metersPerPx(lat0)
  const sx = lngToGX(start[0]),
    sy = latToGY(start[1])
  const gx = lngToGX(goal[0]),
    gy = latToGY(goal[1])
  const dX = gx - sx,
    dY = gy - sy,
    L = Math.hypot(dX, dY) || 1
  const step = Math.max(1, Math.min(TILE, bufCells))

  const seen = new Set<number>()
  const out: Array<[number, number]> = []
  const stamp = (tx: number, ty: number): void => {
    for (let oy = -1; oy <= 1; oy++)
      for (let ox = -1; ox <= 1; ox++) {
        const X = tx + ox,
          Y = ty + oy
        if (X < 0 || Y < 0) continue
        const k = tileKey(X, Y)
        if (!seen.has(k)) {
          seen.add(k)
          out.push([X, Y])
        }
      }
  }
  for (let along = -bufCells; along <= L + bufCells; along += step) {
    const t = along / L
    stamp(Math.floor((sx + dX * t) / TILE), Math.floor((sy + dY * t) / TILE))
  }
  return out
}

/**
 * Off-thread corridor DEM route. Pre-fetches the corridor's DEM tiles through the
 * Dexie cache (offline-safe), then runs the A* in a worker. Returns null on no-route
 * or worker failure (caller falls back to a straight segment). Drop-in for the old
 * in-thread `demRoute`.
 */
export async function demRouteWorker(
  start: LL,
  goal: LL,
  tune: DemTuning = {}
): Promise<LL[] | null> {
  const keys = corridorTileKeys(start, goal, tune)
  const tiles: DemTile[] = []
  await Promise.all(
    keys.map(async ([X, Y]) => {
      const rgba = await demTileCache.getTile(DEM_Z, X, Y)
      if (rgba) tiles.push({ k: tileKey(X, Y), rgba })
    })
  )
  if (tiles.length === 0) return null

  const w = ensureWorker()
  const id = ++counter
  return new Promise<LL[] | null>(resolve => {
    pending.set(id, resolve)
    // No transfer list: structured-clone copies the bytes so the Dexie-backed cache
    // keeps its decoded tiles for the next leg. The copy is a cheap memcpy; the
    // per-pixel decode + A* now happen in the worker.
    const req: DemRouteRequest = { id, start, goal, tune, tiles }
    w.postMessage(req)
  })
}
